import { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Users, ClipboardList, Gamepad2, Settings, TrendingDown, LogOut, Menu, X, ShieldAlert, Megaphone, Mail, Brain, BarChart2, Beaker, Briefcase, FolderKanban, LifeBuoy, Receipt } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import SwipeOpinionLogo from '../../components/brand/SwipeOpinionLogo';
import { fetchCustomQuestions } from '../../lib/adminApi';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/members', label: 'Members', icon: Users },
  { to: '/admin/care', label: 'Member Care', icon: LifeBuoy },
  { to: '/admin/redemptions', label: 'Redemptions', icon: Receipt },
  { to: '/admin/fraud', label: 'Fraud', icon: ShieldAlert },
  { to: '/admin/buyer-quality', label: 'Buyer Quality', icon: BarChart2 },
  { to: '/admin/buyers', label: 'Buyers', icon: Briefcase },
  { to: '/admin/projects', label: 'Projects', icon: FolderKanban },
  { to: '/admin/surveys', label: 'Surveys', icon: ClipboardList },
  { to: '/admin/simulator', label: 'Simulator', icon: Beaker },
  { to: '/admin/games', label: 'Games', icon: Gamepad2 },
  { to: '/admin/ua', label: 'UA', icon: Megaphone },
  { to: '/admin/email', label: 'Email', icon: Mail },
  { to: '/admin/profile-intelligence', label: 'Profile Intelligence', icon: Brain },
  { to: '/admin/config', label: 'Config', icon: Settings },
  { to: '/admin/breakage', label: 'Breakage', icon: TrendingDown },
];

export default function AdminLayout() {
  const { signOut, session } = useAuth();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show a count badge next to "Profile Intelligence" when there are
  // active custom questions configured. Refetches every 5 minutes.
  const { data: customQuestionsData } = useQuery({
    queryKey: ['admin', 'sidebar-custom-questions'],
    queryFn: fetchCustomQuestions,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    enabled: !!session,
  });
  const activeCustomQuestionCount = customQuestionsData?.entries.filter((q) => q.active).length ?? 0;

  const sidebar = (
    <div className="flex flex-col h-full bg-slate-900 text-white w-64">
      <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-2">
        {/* Brand mark + light wordmark on the dark sidebar. The old <Shield> sat
            in the brand-mark slot; the explicit "Admin" pill (kept) is what
            actually signals the admin context, so swapping the mark loses no
            admin signal. */}
        <SwipeOpinionLogo variant="full" markVariant="white" markClassName="w-6 h-6" wordmarkClassName="text-lg text-white" />
        <span className="text-[10px] bg-primary px-2 py-0.5 rounded-full font-medium ml-1">Admin</span>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map(({ to, label, icon: Icon, exact }) => {
          const active = exact ? pathname === to : pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                active
                  ? 'bg-primary text-white font-medium'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{label}</span>
              {to === '/admin/profile-intelligence' && activeCustomQuestionCount > 0 && (
                <span className="text-[10px] font-bold bg-amber-500 text-slate-900 px-1.5 py-0.5 rounded-full">
                  {activeCustomQuestionCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-slate-700">
        <p className="text-xs text-slate-400 truncate mb-2">{session?.user.email}</p>
        <div className="flex gap-2">
          <Link to="/dashboard" className="text-xs text-slate-400 hover:text-white">Member View</Link>
          <span className="text-slate-600">|</span>
          <button onClick={signOut} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <LogOut className="w-3 h-3" /> Logout
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block flex-shrink-0">{sidebar}</div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">{sidebar}</div>
        </>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="text-white">
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold text-white font-display">Admin</span>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
